import * as THREE from 'three';
import { Arm, HAND_POSES } from './hands.js';
import { buildClips, makeSampleResult } from './clips.js';
import { triCount, mergeAll } from './geometry.js';
import {
  Spring,
  Spring3,
  Noise1,
  clamp,
  clamp01,
  lerp,
  damp,
  smootherstep,
  wrapPi,
  TAU,
} from './mathx.js';

/**
 * THE VIEWMODEL RIG.
 *
 * Everything the player looks at for the whole game happens in this file. It is
 * a stack of *additive procedural layers* over one base pose — no baked clips
 * for anything continuous:
 *
 *   base      hip / ADS / sprint / low-ready pose blend
 *   sway      layered incommensurate noise, so idle never visibly loops
 *   bob       stride-driven figure-eight, scaled by speed and stance
 *   lag       the gun TRAILS camera rotation on a spring, overshoots, settles.
 *             This is the single detail that makes a viewmodel feel real.
 *   recoil    per-shot rotational + positional impulse, spring-damper return
 *   clip      keyframed reload / inspect / draw additive offset
 *
 * ADS alignment is computed, not authored: the rig solves for the translation
 * that puts the weapon's sight node exactly on the camera axis at the right eye
 * relief, so the optic is pixel-centred at full ADS whatever the weapon.
 *
 * The scene graph:
 *   viewScene
 *     anchor          <- copies the world camera transform every frame, so the
 *                        gun is lit and shadowed as if it were in the world
 *       rig           <- the animation stack writes here
 *         weapon      <- body meshes + moving-part groups
 *         armL/armR   <- two-bone IK, hands welded to the weapon's grips
 *       reticle       <- collimated dot, placed on the optical axis in camera
 *                        space so it behaves like real glass
 */

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _e = new THREE.Euler(0, 0, 0, 'XYZ');
const _m = new THREE.Matrix4();
const _axisX = new THREE.Vector3(1, 0, 0);
const _axisY = new THREE.Vector3(0, 1, 0);
const _axisZ = new THREE.Vector3(0, 0, 1);

/**
 * Re-shape the curvature vertex masks baked by `materials.bakeMasks`.
 *
 * bakeMasks writes a LINEAR convexity ramp into vColor.rgb (wear / grime / AO).
 * On architecture that is right — a wall has interior vertices for the ramp to
 * fall off against. On chamfered hard-surface geometry there are none, so the
 * ramp runs from the chamfer to the middle of the panel and the whole face
 * reads as worn metal. Raising the exponent collapses that ramp back onto the
 * chamfer's own vertices, which is the outer 1-2 mm of the edge — the only place
 * a rifle actually polishes through.
 *
 * @param {THREE.BufferGeometry} geo   must already carry a `color` attribute
 */
function shapeMasks(geo, o) {
  const col = geo.getAttribute('color');
  if (!col) return geo;
  const a = col.array;
  const amp = [o.wearAmp ?? 1, o.grimeAmp ?? 1, o.aoAmp ?? 1];
  const exp = [o.wearExp ?? 1, o.grimeExp ?? 1, o.aoExp ?? 1];
  for (let i = 0; i < a.length; i += 3) {
    for (let k = 0; k < 3; k++) {
      const v = a[i + k];
      a[i + k] = v <= 0 ? 0 : amp[k] * Math.pow(v > 1 ? 1 : v, exp[k]);
    }
  }
  col.needsUpdate = true;
  return geo;
}

/** Right-handed hand basis from a finger direction and a back-of-hand direction. */
function handBasis(out, finger, back) {
  _v.set(-finger[0], -finger[1], -finger[2]).normalize(); // hand +Z
  _v2.set(back[0], back[1], back[2]);
  _v2.addScaledVector(_v, -_v2.dot(_v));
  if (_v2.lengthSq() < 1e-8) _v2.set(0, 1, 0).addScaledVector(_v, -_v.y);
  _v2.normalize(); // hand +Y
  _v3.crossVectors(_v2, _v).normalize(); // hand +X
  _m.makeBasis(_v3, _v2, _v);
  return out.setFromRotationMatrix(_m);
}

/**
 * The shoulder girdle's pivot, in camera space: the base of the neck, a little
 * below and behind the eye. Both sockets swing about this when the player
 * walks (see `_solveHands`).
 */
const _chest = new THREE.Vector3(0, -0.30, 0.16);
const _torsoQ = new THREE.Quaternion();
const _torsoE = new THREE.Euler();
const _recoil = new THREE.Vector3();
const _relaxA = new THREE.Vector3();
const _relaxB = new THREE.Vector3();
const _relaxAxis = new THREE.Vector3();

/**
 * PER-WEAPON OVERRIDE of the glove size, [trigger, support].
 *
 * The defaults (0.62 / 0.82, see `handScale` below) exist for one reason: to
 * stop the rebuilt glove covering the gun. Where that reason is weaker the
 * price is paid for nothing, because all the shrinking does is cost visible
 * hand on the poses where the hand IS the subject. Three exceptions, each
 * measured against the weapon's own unoccluded pixels:
 *
 *   fists    1.00  there is no weapon, so the floor is vacuous. The default
 *                  took the glove from main's 190k pixels to 100k and gained
 *                  nothing, because there was nothing to gain.
 *   sawnoff  1.00  a short wide receiver held close: at full size the weapon
 *                  still keeps 88% of main's pixels, and the glove goes from
 *                  83% of main's to 152%.
 *   knife    0.80  on a cliff, and this is the ledge. At 1.00 the fist covers
 *                  the blade (17 pixels of knife against main's 175); at the
 *                  0.62 default the hand falls out of the bottom-right corner
 *                  the knife idles in and the blade floats unattached. 0.80
 *                  is the value that renders as a blade in a gloved fist.
 */
const HAND_SCALE = { fists: [1, 1], knife: [0.8, 0.8], sawnoff: [1, 1] };

/**
 * WHERE ROUND THE HANDGUARD THE SUPPORT PALM SITS, per weapon, in degrees
 * measured from straight up and positive toward the OUTBOARD side (the
 * shooter's left, which is the side his support hand comes from).
 *
 * This is authored rather than solved, and that is the lesson of round three.
 * A global score for "where should the hand go" optimises whatever it is told
 * to optimise and produces a different wrong answer on each weapon: scoring
 * fingertips-on-the-far-side buried the hand behind the gun, scoring
 * back-of-hand-to-camera put it under the barrel. Where a hand goes on a
 * particular weapon is a fact about that weapon — where its handguard ends,
 * what is above it, where the magazine is — and eight numbers are cheaper and
 * more honest than a scoring function that has to infer all of it.
 *
 * 0 is the top of the tube, 90 is straight outboard, so the top-outboard
 * quadrant the reference photo shows is 30-60. The fingers wrap from there
 * over the top and down the far side, which is what puts the proximal
 * segments above the weapon's silhouette.
 */
const SUPPORT_CLOCK = {
  rifle: 42,
  ak: 42,
  dmr: 42,
  lmg: 45,
  sniper: 42,
  amr: 45,
  shotgun: 48,
  sawnoff: 48,
  smg: 45,
  rpg: 50,
};
/** Weapons whose handguard node cannot support an over-the-top grip. */
const SUPPORT_CLOCK_DEFAULT = 45;
/**
 * A different clock DOWN THE SIGHTS, for the weapons whose sights are irons.
 *
 * At ADS the eye looks along the bore, so the top-outboard quadrant that reads
 * best at the hip is exactly where the rear notch is looking. MEASURED on the
 * ak: main shows 11 pixels of hand in its ADS frame and this branch showed
 * 1 129, with the glove's bounding box INSIDE the rear aperture. An optic is
 * fine — it sits high enough that the hand is nowhere near the glass — so this
 * is only for the iron-sighted ones, and it moves the hand outboard and down
 * rather than changing the grip.
 */
const SUPPORT_CLOCK_ADS = { ak: 78, shotgun: 78, sawnoff: 78 };

/**
 * NO GLOBAL HIP OFFSET. Round three raised and shifted every weapon's authored
 * `hipPos` to get the arms further into frame, and it worked on the metric and
 * failed on the pictures: the pistol ended up swallowed by its own glove and
 * the shotgun's ADS frame was an empty cone. Weapon placement is a per-weapon
 * decision that was already made; this file does not get to override it.
 */
const HIP_OFFSET = { x: 0, y: 0, z: 0 };
/**
 * How far `_alignTriggerWrist` may pitch the hand off its authored grip frame,
 * radians. SWEPT against the measured trigger break on the shotgun: 1.0 rad
 * leaves 73 degrees, 1.5 leaves 59.5. The cap exists because the compensation
 * is the palm arch, and past about 1.5 rad the heel of the hand has swung far
 * enough off the back strap to be its own defect.
 */
/**
 * How far `_alignTriggerWrist` may pitch the hand off its authored grip frame,
 * radians, and how much of that the palm arch takes back.
 *
 * 0.6 rad, and the cap is the whole design. The rotation does not just
 * straighten the wrist — it carries the fingers with it, and the palm arch can
 * only take back the part that is flexion about the knuckle row. RENDERED at
 * three settings on the rifle, which is the only way to choose between them:
 *
 *   0.6 rad   trigger wrist 76.6 deg, hand ON the grip
 *   1.2       42.2, glove lifting off the receiver with the fingers splayed
 *   2.6       22.8, glove floating clear of the weapon entirely
 *
 * The numbers improve monotonically and the pictures get worse monotonically,
 * which is exactly the trap this card has fallen into twice. A 70-degree wrist
 * on a pistol grip is unremarkable; a hand holding nothing is not. The real
 * fix is the authored `gripR.finger` on each weapon — it points 33 degrees
 * below the horizontal where the metacarpals should run forward to the
 * knuckles — and that is fifteen files of work against renders, not a solver.
 *
 * ALIGN_COMP is 0.75 rather than 1: taking the whole pitch back through the
 * arch folds the palm double at these angles. At 0.75 the fingers rotate a
 * quarter of the pitch relative to the grip, which they can absorb — they are
 * wrapped around it, not keyed to it — and the palm stays a palm.
 */
const ALIGN_MAX = 0.6;
/**
 * Down the sights the budget is the same, and the guard below matters more
 * than the number. A hand that ends up AT THE LENS in ADS — the rifle's grip
 * sits at camera Z +0.01 down the sights — cannot be improved by rotating it:
 * MEASURED, 0.6 rad of alignment took the trigger glove from 1.8% of the frame
 * on main to 14.8%, and 2.0 rad filled every pixel of it. The hand was not
 * fixed, it was turned to face the camera from two centimetres away.
 */
const ALIGN_MAX_ADS = 0.6;
const ALIGN_COMP = 0.75;

export class Viewmodel {
  constructor(ctx, mats) {
    this.ctx = ctx;
    this.mats = mats;
    this.rng = ctx.rng.fork();

    this.anchor = new THREE.Object3D();
    this.anchor.name = 'ow-viewmodel-anchor';
    this.rig = new THREE.Object3D();
    this.rig.name = 'ow-viewmodel-rig';
    this.anchor.add(this.rig);
    ctx.viewScene.add(this.anchor);

    // ---- arms -------------------------------------------------------------
    /**
     * GLOVED HANDS, BARE FOREARMS, GREEN SLEEVE HEM — all three at once.
     *
     * The history here is two wrong turns in opposite directions, so be
     * specific about which surface is which:
     *
     *  - 8534d2a pointed the HAND at the skin materials and set `bare: true`.
     *    Wrong: the card's reference photo (ttk-map-voting-02.webp) is a padded
     *    tactical glove with knuckle armour, quilted finger backs, grip patches
     *    and a strapped cuff. `bare` no longer exists; the glove geometry was
     *    rebuilt around it in hands.js.
     *  - Everything before that clothed the whole LIMB in green, which the card
     *    never asked for: "textured arms with skin color and t-shirt where top
     *    of sleeve is green and visible". The forearm is skin; the green is the
     *    shirt sleeve over the elbow end of it, and its hem is what you see.
     *    See the note on `this.fore` in hands.js.
     *
     * `sleeve` stays green on purpose — `sleeve` in materials.js documents it
     * as the player's visible team colour, which is a deliberate feature the
     * reference photo (a clip from an unrelated game) was never meant to
     * override.
     */
    const handMats = {
      glove: mats.get('glove'),
      pad: mats.get('glove_pad'),
      seam: mats.get('glove_seam'),
      sleeve: mats.get('sleeve'),
      skin: mats.get('skin'),
    };
    // Shoulder joints in CAMERA space: ~200 mm lateral, ~210 mm below the eye
    // and only just behind it.
    //
    // Two constraints fight here. Too far BACK and a 570 mm arm cannot reach the
    // handguard, so the two-bone solve clamps and the elbow locks dead straight
    // — the "broomstick arm". Too far FORWARD (a properly bladed stance) and the
    // upper arm itself lands inside the near frustum, so a 100 mm-wide sleeve
    // fills half the screen. The support hand is therefore placed on the REAR of
    // the handguard instead, which buys the reach without moving the joint into
    // shot.
    /**
     * SHOULDER SOCKETS AND BONE LENGTHS ARE SWEPT, not guessed.
     *
     * Both are pure cosmetic parameters — the sockets are hidden behind the
     * camera and the upper arm is almost never in frame — and both decide the
     * wrist break, because the elbow circle they define is what the solve in
     * `Arm.solve` gets to choose from. `results/sweep.mjs` scans them against
     * the worst break over all fifteen weapons:
     *
     *   trigger shoulder Y  -0.20 -> -0.28   worst break 31.3 -> 22.7 deg
     *   support bones       x1.00 -> x1.18   worst break 60.2 -> 50.7 deg,
     *                                        mean 51.5 -> 30.3 deg
     *
     * The trigger socket drops 80 mm. That is lower than a real shoulder sits
     * relative to the eye, and it is the right trade: the grip is only 260 mm
     * from the socket, so the arm is folded to 41% of its length and the elbow
     * is thrown a quarter of a metre off the shoulder-to-wrist line. Where that
     * elbow lands decides the forearm's direction, and from a higher socket the
     * whole circle of candidates pointed the forearm up at the hand instead of
     * along it.
     */
    this.armR = new Arm(1, handMats, {
      scale: 1,
      // See `handScale` in hands.js: the trigger glove is the one in front of
      // the weapon, so it is the one that has to stop covering it.
      /**
       * SWEPT against the weapon's own visible pixels, every weapon, because
       * the trigger glove is the thing in front of the gun. Unoccluded weapon
       * pixels as a fraction of main's, at idle:
       *
       *   handScale   shotgun  rifle  sniper  dmr   lmg
       *   1.00         0.30     0.50   0.50   0.55  0.60
       *   0.82         0.45     0.64   0.59   0.66  0.74
       *   0.72         0.51     0.74   0.68   --    --
       *   0.62         0.65     0.83   0.79   --    --   <- chosen
       *
       * 0.62 is the first value that clears the 0.65 floor on the shotgun,
       * which is the worst case because its receiver is small and the grip
       * sits right on it. The glove is still 1.6-2.2x more visible than
       * main's; what it has stopped doing is covering the weapon.
       */
      handScale: 0.62,
      shoulderX: 0.205,
      shoulderY: -0.22,
      shoulderZ: 0.3,
      pose: 'grip',
    });
    // The shoulders stay BEHIND the eye. Blading the support shoulder forward to
    // reach the handguard was tried and measured: at z=-0.075 the 89 mm forearm
    // sleeve crosses the frame diagonally and hides the barrel and the muzzle,
    // which is precisely the failure the note above warns about. The reach is
    // bought by cheating the bones 10% long instead — see hands.js L_UPPER.
    /**
     * The support arm's bones are cheated 18% long on top of the 10% already in
     * `L_UPPER` (see hands.js). The reason is the same one as the original
     * cheat, only sharper: this arm reaches the handguard at 95-109% of its own
     * length, and at full extension the elbow circle collapses to a point — the
     * forearm direction stops being a free variable at all, so the elbow solve
     * has nothing to choose and the wrist stays wherever the anchor put it.
     * Lengthening the chain reopens the circle. SWEPT: it takes the mean break
     * over the catalog from 51.5 to 30.3 degrees, and it pushes the elbow
     * further out of frame rather than into it, because a longer chain between
     * two fixed endpoints bends more.
     */
    this.armL = new Arm(-1, handMats, {
      scale: 0.97,
      // The support glove is on the handguard rather than in front of the
      // receiver, so it costs the weapon far less and keeps more of its size.
      handScale: 0.82,
      shoulderX: 0.2,
      shoulderY: -0.22,
      shoulderZ: 0.02,
      upper: 0.3894,
      fore: 0.354,
      pose: 'clamp',
    });
    this.rig.add(this.armR.root);
    this.rig.add(this.armL.root);
    /**
     * The arms get the SAME curvature-mask treatment the weapon does. Without
     * this every wear/grime/AO number in `sleeve`, `glove`, `glove_pad` and
     * `glove_seam` is dead code — see Arm.bakeSurfaceMasks. It has to happen
     * before `_fitSupportHand` runs, because that adds contact AO into the same
     * attribute with Math.max and would otherwise be overwritten.
     */
    const bakeArms = this.mats.lib?.bakeMasks?.bind(this.mats.lib) ?? null;
    if (bakeArms) {
      this.armR.bakeSurfaceMasks(bakeArms, shapeMasks, this.rng);
      this.armL.bakeSurfaceMasks(bakeArms, shapeMasks, this.rng);
    }
    // Body-fixed shoulders, expressed in camera space and re-based into rig
    // space every frame so the elbows do not swing when the gun moves.
    // The trigger shoulder sits 0.3 m behind the eye. RENDERED, 2026-10-14
    // (dc709e6): at z = 0.06 it was only 0.26 m from the grip, so a 0.57 m arm
    // folded shut, the elbow dropped straight under the wrist and the forearm
    // met the hand at 90-140 deg. This arm is almost entirely out of frame, so
    // moving its socket back costs nothing on screen and lets the elbow open
    // up. The 40 mm drop on top of it is this branch's sweep (see the Arm
    // construction above); the two are the same fix from two directions and
    // both are kept.
    this.shoulderR = new THREE.Vector3(0.205, -0.22, 0.3);
    this.shoulderL = new THREE.Vector3(-0.2, -0.22, 0.02);

    // ---- reticle ----------------------------------------------------------
    this.reticle = new THREE.Object3D();
    this.reticle.name = 'ow-reticle';
    this.anchor.add(this.reticle);
    /**
     * A 2 MOA dot with a soft halo, a dark keyline and a 12-segment outer ring.
     *
     * All four are authored at UNIT radius and scaled together by the dot's
     * angular size in `_updateReticle`, so the whole reticle is one shape that
     * grows and shrinks as a unit — proportions can never drift.
     *
     *   core     the emitter. 0xff1a08 at intensity 1.35, and the intensity is
     *            measured — twice, because the first analysis was wrong.
     *
     *            The old note here reasoned about how much GREEN the emitter adds
     *            and concluded 3.2 was safe. It is not, and the reason is the tone
     *            curve, not the additive maths: the frame is graded with AgX (see
     *            render/composite.js), and AgX's whole signature is that it
     *            DESATURATES as it approaches display white. A pixel whose red
     *            channel is 3.2 over a mid-grey background comes out of the curve
     *            at rgb(255,248,239) whatever its green and blue were — measured on
     *            ads.png, a white dot, exactly what the previous note said it had
     *            fixed. There is no colour available up there.
     *            0.95 keeps the core on the near-linear part of the curve, where a
     *            saturated red stays a saturated red, and the halo + ring below are
     *            what carry the "it is an emitter" read instead of raw radiance.
     *   halo     1.6x the core radius, ~6% alpha. This is the bloom seed and the
     *            reason the dot looks like it is BEHIND glass. It has to stay
     *            tight; the old 0.0095 rad / 0.34 alpha halo was 23 px across and
     *            was what actually read on screen — a soft salmon blob.
     *   rim      a NORMALLY blended dark keyline. Additive blending cannot draw
     *            anything darker than what is behind it, so without a separate
     *            ring the dot dissolves the moment it crosses a blown-out sky.
     *   ring     12 arc segments at 3.2x the dot radius, 35% of its radiance.
     *            A bare dot at 8 px is indistinguishable from a dead subpixel;
     *            the segmented ring is what makes it read as an EMITTER, and it
     *            is the standard 65 MOA circle-dot every modern sight ships.
     */
    const core = new THREE.CircleGeometry(1, 32);
    const halo = new THREE.CircleGeometry(1.6, 32);
    const rim = new THREE.RingGeometry(1, 1.42, 32, 1);
    const RING_SEGS = 12;
    const ringArcs = [];
    for (let i = 0; i < RING_SEGS; i++) {
      const a0 = (i / RING_SEGS) * TAU;
      ringArcs.push(new THREE.RingGeometry(2.98, 3.42, 4, 1, a0, (TAU / RING_SEGS) * 0.56));
    }
    const ring = mergeAll(ringArcs);
    this._reticleGeo = [core, halo, rim, ring];
    this.dotCore = new THREE.Mesh(core, mats.reticle(0xff1206, 0.95));
    this.dotHalo = new THREE.Mesh(halo, mats.reticle(0xff2a0c, 0.34));
    this.dotRim = new THREE.Mesh(rim, mats.reticleOutline(0.85));
    this.dotRing = new THREE.Mesh(ring, mats.reticle(0xff1206, 0.95 * 0.5));
    this.dotHalo.renderOrder = 19;
    this.dotRim.renderOrder = 20;
    this.dotRing.renderOrder = 20;
    this.dotCore.renderOrder = 21;
    this.reticle.add(this.dotHalo);
    this.reticle.add(this.dotRim);
    this.reticle.add(this.dotRing);
    this.reticle.add(this.dotCore);
    for (const m of [this.dotCore, this.dotHalo, this.dotRim, this.dotRing]) {
      m.frustumCulled = false;
      m.userData.owNoPrepass = true;
      m.userData.owNoShadow = true;
    }

    // ---- animation state --------------------------------------------------
    this.weapons = new Map();
    this.active = null;

    this.adsT = 0;
    this.adsTarget = 0;
    this.sprintT = 0;
    this.lowReadyT = 0;
    this.bobPhase = 0;
    this.stepT = 0;
    this.noiseT = 0;
    this.triggerT = 0;
    this.triggerTarget = 0;

    this.lag = new Spring3(5.4, 0.46);
    this.lagRot = new Spring3(6.2, 0.42);
    this.recPos = new Spring3(9, 0.42);
    this.recRot = new Spring3(9, 0.42);
    this.jumpSpring = new Spring(5.5, 0.5);
    this.landSpring = new Spring(7.5, 0.55);
    this.settle = new Spring3(2.2, 0.7);

    this.noise = [];
    for (let i = 0; i < 6; i++) this.noise.push(new Noise1(this.rng, 512));
    this.noiseRates = [0.13, 0.19, 0.271, 0.083, 0.117, 0.163];

    this._angVel = { yaw: 0, pitch: 0 };
    this._prevYaw = 0;
    this._prevPitch = 0;
    this._hasPrev = false;

    // clip playback
    this.clip = null;
    this.clipT = 0;
    this.clipPrevT = 0;
    this.clipResult = makeSampleResult();
    this.onClipEvent = null;

    // moving-part drive
    this.boltCycle = 0; // 0..1, driven by firing
    this.boltHold = 0; // 1 = locked back (empty)
    this.magInHand = 0;
    this.magVisible = true;

    // preallocated working state
    this._basePos = new THREE.Vector3();
    this._baseQuat = new THREE.Quaternion();
    this._adsPos = new THREE.Vector3();
    this._adsQuat = new THREE.Quaternion();
    this._tmpPos = new THREE.Vector3();
    this._tmpQuat = new THREE.Quaternion();
    this._handPos = new THREE.Vector3();
    this._handQuat = new THREE.Quaternion();
    this._handPosL = new THREE.Vector3();
    this._handQuatL = new THREE.Quaternion();
    this._sightLocal = new THREE.Vector3();
    this._lhandTarget = new THREE.Vector3();
    this._lhandFinger = [0, 0, 0];
    this._lhandBack = [0, 0, 0];
    this._muzzleWorld = new THREE.Vector3();
    this._muzzleDir = new THREE.Vector3();
    this._ejectWorld = new THREE.Vector3();
    this._ejectVel = new THREE.Vector3();

    this.debugFrozen = false;
    /** Set false by the preview harness to leave the cameras alone. */
    this.trackCamera = true;
    this.rigOverride = null;
    this._scriptedFire = -1;
    this._scriptShots = 0;
  }

  /* ====================================================================== */
  /*  construction                                                          */
  /* ====================================================================== */

  /**
   * Turn a model description (body + moving assemblies + nodes) into meshes.
   * One mesh per material per assembly: a whole rifle lands in 7-9 draw calls.
   */
  addWeapon(model, def) {
    const group = new THREE.Object3D();
    group.name = `weapon-${model.id}`;
    group.visible = false;
    this.rig.add(group);

    let tris = 0;
    const meshes = [];
    const bake = this.mats.lib?.bakeMasks?.bind(this.mats.lib) ?? null;

    const build = (asm, parent, wearScale = 1) => {
      const map = asm.build();
      for (const [matKey, geo] of map) {
        // Curvature masks: convex chamfers wear to bright metal, creases fill
        // with grime. This is what stops the gun reading as clean plastic.
        if (bake) {
          /**
           * Chamfered hard-surface geometry has no interior vertices on a face,
           * so a per-vertex edge mask interpolates linearly from the chamfer all
           * the way to the far side of the panel: a rail tooth, a mount top face
           * or a handguard slat comes out uniformly worn, which is what turned
           * the rail teeth into flat near-white bars and the mount into beige MDF.
           *
           * Bake the mask at full amplitude and then SHAPE it (below): raising the
           * exponent is the only knob that pulls a vertex-interpolated ramp back
           * onto the outer millimetre or two of the edge, because it pushes
           * everything below the chamfer's own vertices toward zero.
           */
          const soft = matKey === 'polymer' || matKey === 'rubber' || matKey === 'polymer_tan';
          bake(geo, { wear: 1, grime: 1, ao: 1, edgeThreshold: 0.16, rng: this.rng });
          shapeMasks(geo, {
            /**
             * wearAmp comes DOWN and grimeAmp goes UP.
             *
             * With the viewmodel recalibrated to be diffuse-dominant (see
             * materials.js `alu`), the wear layer's contrast against the base
             * albedo is what decides whether a chamfer reads as polished alloy or
             * as a white pencil line, and on small parts — where every vertex is
             * convex — it decides whether a takedown pin reads as steel or as a
             * cream plastic cube. 0.9 -> 0.62 on hard surfaces.
             *
             * Grime is the opposite: it is the only mask that paints the CONCAVE
             * side of the geometry, so it is what puts dirt in the magwell corners,
             * the trigger-guard fillet, the rail slots and the seam between the
             * handguard panels. Those creases were reading perfectly clean, which
             * is a large part of "props read as pasted-on decals" applied to a gun.
             */
            wearAmp: (soft ? 0.42 : 0.62) * wearScale,
            wearExp: soft ? 3.4 : 2.8,
            grimeAmp: 1.15,
            grimeExp: 1.25,
            aoAmp: 1.0,
            aoExp: 1.15,
          });
        }
        const mesh = new THREE.Mesh(geo, this.mats.get(matKey));
        mesh.name = `${asm.name}-${matKey}`;
        // The viewmodel does not cast into the cascades (it is not in the world
        // scene), but it absolutely must RECEIVE the sun shadow: without this the
        // gun is lit at full sun while the street around it is in shade, which is
        // the single most obvious "pasted-on sticker" tell.
        mesh.castShadow = false;
        mesh.receiveShadow = true;
        mesh.frustumCulled = false;
        parent.add(mesh);
        meshes.push(mesh);
        tris += triCount(geo);
      }
    };

    build(model.body, group);

    const parts = {};
    for (const [name, asm] of Object.entries(model.moving)) {
      const sub = new THREE.Object3D();
      sub.name = `${model.id}-${name}`;
      group.add(sub);
      build(asm, sub, name === 'magazine' ? 0.8 : 1);
      parts[name] = sub;
    }

    // Seat the moving parts at their rest transforms.
    const n = model.nodes;
    if (parts.magazine && n.magSeat) applyNode(parts.magazine, n.magSeat);
    if (parts.charging && n.chargeRest) applyNode(parts.charging, n.chargeRest);
    if (parts.bolt && n.boltRest) applyNode(parts.bolt, n.boltRest);
    if (parts.slide && n.slideRest) applyNode(parts.slide, n.slideRest);
    if (parts.trigger && n.triggerPivot) applyNode(parts.trigger, n.triggerPivot);
    if (parts.selector && n.selectorPivot) applyNode(parts.selector, n.selectorPivot);

    const entry = {
      id: model.id,
      def,
      model,
      group,
      parts,
      meshes,
      tris,
      clips: buildClips(model.nodes, def),
      // the sight point and its axis, in weapon space
      sight: new THREE.Vector3().fromArray(model.nodes.sight),
      ironSight: new THREE.Vector3().fromArray(model.nodes.ironSight ?? model.nodes.sight),
      muzzle: new THREE.Vector3().fromArray(model.nodes.muzzle),
      eject: new THREE.Vector3().fromArray(model.nodes.eject),
      ejectDir: new THREE.Vector3().fromArray(model.nodes.ejectDir ?? [1, 0.4, 0.2]).normalize(),
      optic: model.nodes.opticGlass ?? null,
      magSeatPos: new THREE.Vector3().fromArray(model.nodes.magSeat.pos),
      magSeatQuat: new THREE.Quaternion().setFromEuler(
        new THREE.Euler().fromArray(model.nodes.magSeat.rot)
      ),
      gripR: model.nodes.gripR,
      gripL: model.nodes.gripL,
      chargePull: new THREE.Vector3().fromArray(model.nodes.chargePull ?? [0, 0, 0]),
      boltTravel: new THREE.Vector3().fromArray(model.nodes.boltTravel ?? [0, 0, 0]),
      slideTravel: new THREE.Vector3().fromArray(model.nodes.slideTravel ?? [0, 0, 0]),
      triggerPull: model.nodes.triggerPull ?? -0.3,
      magLen: model.magSize?.len ?? 0.2,
      shell: model.shell,
      lhandPose: model.id === 'pistol' ? 'cup' : 'clamp',
    };
    // Both of these need the rig posed; the first does the posing. It also has
    // to run for EVERY weapon, not just the ones with a handguard — calling it
    // from inside _fitSupportHand meant the pistols, the uzi, the knife and the
    // fists got no wrist alignment at all, which is exactly where the 83-100
    // degree breaks were left.
    this._applyHandScale(entry);
    this._alignTriggerWrist(entry, def);
    this._fitSupportHand(entry, def);
    /**
     * And again down the sights. ADS moves the weapon to put its sight on the
     * camera axis while the shoulders stay with the body, so the support hand
     * meets the handguard from a different direction: MEASURED, reusing the
     * hipfire placement left the shotgun's support wrist at 90 degrees in ADS
     * against 36 at the hip. `_alignBoth` already poses the rig that way for
     * the trigger hand; this gives the support hand the same courtesy.
     */
    this._alignBoth(entry, def, true);
    this._fitSupportHand(entry, def, true);
    this._alignBoth(entry, def, false);
    this.weapons.set(model.id, entry);
    return entry;
  }

  /**
   * Collapse both hands to one skinned mesh per material — see `Arm.skin()` for
   * why that is pixel-exact and what it saves (86 of the viewmodel pass's 115
   * draw calls a frame).
   *
   * Has to run HERE and not in the Arm constructor: `_fitSupportHand` bakes
   * contact AO into the left glove's part geometries once per weapon, through
   * each part's own transform chain. Collapsing that chain first would bake the
   * gradient at the bind pose instead of the fitted one.
   */
  finalizeHands() {
    this.armR.skin();
    this.armL.skin();
    return this;
  }

  /**
   * GROUND THE SUPPORT HAND ON THE HANDGUARD — once, at build time.
   *
   * Two halves, and both are needed: geometry alone still reads as two floating
   * objects, and AO alone cannot close a 10 mm gap.
   *
   *  1. `Arm.fitToCylinder` searches each distal joint for the rotation that puts
   *     that fingertip's contact patch on the handguard surface (<=1 mm off, up
   *     to 1.5 mm buried), measured through the real transform chain rather than
   *     derived analytically — see the note there for why the analytic version
   *     was 8-14 mm out in every frame despite the maths being right.
   *  2. The contact points that come back are then used to bake a contact-AO
   *     gradient into BOTH sides of the interface: the handguard here, the glove
   *     in `Arm.bakeContactAO`. 0.55 multiply at the contact, easing to 1.0 over
   *     12 mm.
   *
   * The AO mask lives in vColor.b, which the library's shader turns into
   * `orm.r *= 1 - vColor.b * wear[2]`; wear[2] is 0.5 on every weapon material,
   * so a mask of 0.9 is the 0.55 multiply asked for.
   */
  /**
   * PLACE THE SUPPORT HAND FROM THE HANDGUARD, not from a hand-authored vector.
   *
   * Four separate defects on card 42f58873 were all this one thing:
   *
   *   - the palm heel hung 27-29 mm off the handguard while only the knuckle
   *     row touched, so the hand hinged in mid-air;
   *   - 12.7% of the glove's vertices were up to 22 mm INSIDE the handguard
   *     cylinder, because the fingertip solve was reaching a tube the rest of
   *     the hand was not on;
   *   - the fingers hung under the weapon like a row of sausages instead of
   *     coming over the top and curling down the far side as they do in the
   *     reference photo;
   *   - and the support wrist broke 56-60 degrees, because the authored
   *     metacarpal axis pointed somewhere no forearm could follow.
   *
   * All four are decided by the hand's FRAME on the tube, and a frame on a
   * cylinder has exactly two free parameters: where round the clock the palm
   * sits, and which way round the fingers wrap. Everything else — the palmar
   * normal, the metacarpal axis, the thumb side, the wrist position — follows
   * from those two and the tube's own radius. So they are solved here, once per
   * weapon at build time, instead of being guessed in fifteen model files:
   *
   *   u  the radial direction of the palm contact (the clock angle)
   *   t  the tangential metacarpal axis, +/- round the tube (the wrap)
   *
   *   hand -Y (palmar)   = -u      the palm faces the axis
   *   hand -Z (fingers)  =  t      the metacarpals run round the tube
   *   hand +X (thumb)    = u x t   which puts the thumb along the barrel
   *
   * and the wrist is then placed so the HEEL PAD lands on the surface, which is
   * what the authored anchors could never do because the heel is 60 mm behind
   * the only point they controlled.
   *
   * The scan scores a candidate on where its fingertips end up — on the far
   * side of the tube from the eye and above the weapon's centreline, i.e.
   * wrapped over the top, which is the photo — on keeping the wrist on the near
   * side so the forearm does not pass through the gun, on the wrist break the
   * two-bone solve can then achieve, and on reach. The axial station along the
   * handguard is NOT solved: it stays wherever the weapon's own `gripL` put it,
   * because that is a real per-weapon decision (where the handguard ends, where
   * the magazine is) and nothing here knows better.
   */
  _placeSupportHand(w, def, ads = false) {
    const hg = w.model?.nodes?.handguard;
    const gL = w.gripL;
    if (!hg || !gL || w.id === 'pistol') return false;

    const ax0 = new THREE.Vector3().fromArray(hg.axis);
    const axd = new THREE.Vector3().fromArray(hg.dir).normalize();
    const authored = new THREE.Vector3().fromArray(gL.pos);
    const station0 = authored.clone().sub(ax0).dot(axd);
    /**
     * The station along the tube is scanned too, but only just.
     *
     * It is mostly a real per-weapon decision — where the handguard ends, where
     * the magazine is — so the authored value is strongly preferred and the
     * search is capped at 40 mm and clipped to the handguard's own extent. What it buys
     * is the three long weapons where the support hand was authored at nearly
     * full arm extension: with the elbow circle collapsed to a point the
     * forearm direction stops being a free variable at all, so the elbow solve
     * has nothing to work with and the wrist stays broken however it is scored.
     * Sliding the hand 20-50 mm back toward the shooter restores the slack and
     * the wrist with it, and a support hand you can actually reach is the more
     * plausible placement anyway.
     */
    const zLo = Math.min(hg.z0, hg.z1) + 0.03;
    const zHi = Math.max(hg.z0, hg.z1) - 0.03;
    const centre = new THREE.Vector3();

    /**
     * The eye, in rig space. The rig sits at the weapon's hip offset from the
     * camera, so the camera is at minus that. hipRot is a few degrees and is
     * ignored on purpose: this only decides which side of a 50 mm tube faces
     * the player, and three degrees cannot change that answer.
     */
    /**
     * The eye, in weapon space. `_alignTriggerWrist` runs first and
     * reconstructs the resting rig transform; reuse its answer rather than
     * approximating one, because an approximate eye gives a confidently wrong
     * visibility probe.
     */
    const eye = (this._buildEye ?? new THREE.Vector3()).clone();
    // Which side of the tube faces the player. Taken at the authored station;
    // sliding the hand a few centimetres along the axis cannot change it.
    const near = eye.clone().sub(ax0.clone().addScaledVector(axd, station0));
    near.addScaledVector(axd, -near.dot(axd));
    if (near.lengthSq() < 1e-8) near.set(0, 1, 0);
    near.normalize();
    const up = new THREE.Vector3(0, 1, 0);
    up.addScaledVector(axd, -up.dot(axd));
    if (up.lengthSq() < 1e-8) up.set(0, 1, 0);
    up.normalize();

    // Which way round the tube "outboard" is: the support hand comes from the
    // shooter's left, so a positive clock angle rotates `up` toward -X.
    const side = Math.sign(up.clone().cross(axd).x) || 1;
    const heel = new THREE.Vector3().fromArray(this.armL.palmContact.heel);
    const shoulder = this.armL.shoulder;
    const reach = (this.armL.l1 + this.armL.l2) * 0.985;

    /**
     * `up`, rotated to the authored clock angle about the tube. The sign is
     * fixed too: with the palm in the upper-outboard quadrant the fingers have
     * to run toward the top of the tube and down its far side, which is one of
     * the two directions, not a free choice.
     */
    const clockDeg = (ads ? SUPPORT_CLOCK_ADS[w.id] : undefined) ?? SUPPORT_CLOCK[w.id] ?? SUPPORT_CLOCK_DEFAULT;
    const clock = (clockDeg * Math.PI) / 180;
    const u = new THREE.Vector3();
    const t = new THREE.Vector3();
    const hx = new THREE.Vector3();
    const tip = new THREE.Vector3();
    const basis = new THREE.Matrix4();
    const quat = new THREE.Quaternion();
    const pos = new THREE.Vector3();
    const wristRadial = new THREE.Vector3();
    const foreDir = new THREE.Vector3();
    const handZ = new THREE.Vector3();

    const cands = [];
    const stations = [];
    for (let k = -4; k <= 4; k++) {
      const z = station0 + (k * 0.04) / 4;
      if (zLo <= zHi && (z < zLo || z > zHi)) continue;
      stations.push(z);
    }
    if (!stations.length) stations.push(station0);
    for (const station of stations) {
      centre.copy(ax0).addScaledVector(axd, station);
      /**
       * How far the hand is from the eye, which is most of how big it is on
       * screen. MEASURED: the support glove came to 96 x 94 px on the shotgun
       * — about 1.7% of the frame if nothing covered it — because the authored
       * station is 0.57 m out along the handguard, and apparent size is
       * linear in that. The reference photo's support hand is roughly 10% of
       * its frame. Drawing the grip back toward the receiver is both the
       * cheapest way to get it and an entirely ordinary place to hold a pump.
       */
      const stationCost = Math.abs(station - station0) * 8;
      {
      {
        const sign = -1;
        u.copy(up).applyAxisAngle(axd, clock * side).normalize();
        t.crossVectors(axd, u).multiplyScalar(sign).normalize();
        // hand +Z is opposite the metacarpal direction; +Y is the dorsal side.
        hx.crossVectors(u, t.clone().negate()).normalize();
        basis.makeBasis(hx, u, t.clone().negate());
        quat.setFromRotationMatrix(basis);
        // Seat the heel pad 1.5 mm proud of the surface.
        pos.copy(centre).addScaledVector(u, hg.r + 0.0015);
        pos.sub(heel.clone().applyQuaternion(quat));

        /**
         * THE SUPPORT HAND IS THE PHOTO'S SUBJECT, so the score is about
         * whether you can SEE it, not about where its fingertips end up.
         *
         * The previous version scored `far` — fingertips on the opposite side
         * of the tube from the eye — which is a faithful reading of "the
         * fingers wrap over the top and down the far side" and a disaster in
         * practice: it put the whole hand behind the weapon. MEASURED, the
         * unoccluded support glove came to 1 225 px on the shotgun and 332 on
         * the sawnoff, 0.06-0.13% of the frame against about 10% in the
         * reference photo, and a wire render showed a cuff, one knuckle plate
         * and no fingers at all.
         *
         * What makes a gripping hand legible is the BACK of it facing the eye
         * with the proximal finger segments breaking the weapon's silhouette
         * on the way over. Both are properties of the frame, so both are
         * scored directly:
         *
         *   backToCam   the dorsal direction `u` points at the eye
         *   proximalUp  the metacarpal axis `t` leaves the palm upward, so the
         *               knuckles cross the top of the tube rather than hiding
         *               under it
         *
         * The fingertips still finish on the far side — that is what wrapping
         * IS — but it falls out of the geometry instead of being asked for.
         */
        const backToCam = u.dot(near);
        const proximalUp = t.dot(up);
        wristRadial.copy(pos).sub(centre);
        wristRadial.addScaledVector(axd, -wristRadial.dot(axd));
        const wristNear = wristRadial.lengthSq() > 1e-9 ? wristRadial.clone().normalize().dot(near) : 0;
        const d = pos.distanceTo(shoulder);
        if (d > reach) continue;

        // The wrist break the elbow scan will be able to reach, from the law of
        // cosines on the solve's own triangle (see Arm.solve).
        const l1 = this.armL.l1;
        const l2 = this.armL.l2;
        const cosW = THREE.MathUtils.clamp((d * d + l2 * l2 - l1 * l1) / (2 * d * l2), -1, 1);
        const coneToWS = Math.PI - Math.acos(cosW);
        handZ.set(0, 0, -1).applyQuaternion(quat);
        const ws = shoulder.clone().sub(pos).normalize();
        const handToWS = Math.acos(THREE.MathUtils.clamp(handZ.dot(ws), -1, 1));
        const bestBreak = Math.abs(coneToWS - handToWS);

        /**
         * NO PROXIMITY TERM. Round three added one — nearer is bigger is more
         * visible — and the gate caught what it really bought: pulling the
         * support hand back along the handguard brings it toward the camera,
         * and on the rifle it took the unoccluded glove from 7 628 pixels to
         * 36 047 by putting it IN FRONT OF THE WEAPON, which went from 71 324
         * visible pixels to 33 056. A hand you can see because it is covering
         * the gun is not a win.
         */
        const score =
          backToCam * 3.0 +
          proximalUp * 2.0 +
          wristNear * 1.0 -
          Math.max(0, bestBreak - 0.42) * 12 -
          Math.max(0, d - reach * 0.93) * 20 -
          stationCost;
        cands.push({
          score,
          pos: pos.clone(),
          quat: quat.clone(),
          // Five points over the back of the hand: the dorsum itself, one
          // either side along the tube, and one either side around it. One
          // point answers "is this spot visible"; five answer "is the HAND".
          samples: [
            centre.clone().addScaledVector(u, hg.r + 0.055),
            centre.clone().addScaledVector(u, hg.r + 0.05).addScaledVector(axd, 0.035),
            centre.clone().addScaledVector(u, hg.r + 0.05).addScaledVector(axd, -0.035),
            centre.clone().addScaledVector(u, hg.r + 0.045).addScaledVector(t, 0.04),
            centre.clone().addScaledVector(u, hg.r + 0.045).addScaledVector(t, -0.04),
          ],
          backToCam,
          proximalUp,
          wristNear,
          bestBreak,
          d,
          station,
          sign,
        });
      }
      }
    }
    if (!cands.length) return false;
    /**
     * CAN THE EYE ACTUALLY SEE IT? Everything above is geometry about the tube;
     * this is the only term that knows the rest of the weapon exists.
     *
     * It has to, because the geometric answer and the visible answer differ by
     * the whole width of the gun. On a pump shotgun the handguard node IS the
     * magazine tube, the barrel sits directly on top of it, and the side of
     * that tube facing the player is therefore the side with the barrel in
     * front of it. Scoring "back of the hand toward the camera" alone put the
     * glove in exactly that gap: MEASURED, 0.01% of the frame — a hand placed
     * perfectly, inside the gun.
     *
     * So the best few placements by shape are ray-tested against the weapon's
     * own meshes, and the first one the eye can actually reach wins. A ray each
     * for the twenty best candidates is build-time work the renderer would
     * otherwise have to tell us about one screenshot at a time.
     */
    cands.sort((a, b) => b.score - a.score);
    /**
     * The probe casts in WORLD space, but every point above is in weapon
     * space. They are only the same frame if the weapon group happens to sit
     * at the origin, and relying on that is how the probe came to report 100%
     * clear on a hand the raster showed 97% covered. Force the group's world
     * matrix up to date and carry the points through it.
     */
    this.rig.updateMatrixWorld(true);
    w.group.updateMatrixWorld(true);
    const toWorld = w.group.matrixWorld;
    const eyeW = eye.clone().applyMatrix4(toWorld);
    const probe = new THREE.Raycaster();
    const dir = new THREE.Vector3();
    const spW = new THREE.Vector3();
    let best = null;
    let bestFinal = -Infinity;
    for (const cand of cands.slice(0, 24)) {
      let clear = 0;
      for (const sp of cand.samples) {
        spW.copy(sp).applyMatrix4(toWorld);
        dir.copy(eyeW).sub(spW);
        const dist = dir.length();
        if (dist < 1e-4) continue;
        dir.divideScalar(dist);
        probe.set(spW, dir);
        probe.far = dist;
        const hits = probe.intersectObject(w.group, true);
        // 30 mm of slack: a hand grazing the handguard it is holding is not an
        // occlusion, and the contact solve puts it within a millimetre.
        if (!hits.length || hits[0].distance > 0.03) clear++;
      }
      cand.clear = clear / cand.samples.length;
      /**
       * Visibility is SCORED, not filtered. A filter takes the first candidate
       * that passes, which on a pump shotgun is the first one whose single
       * probe point happens to miss the barrel — and the rest of the hand stays
       * behind it: MEASURED, the support glove projected 92 x 94 px and 83% of
       * that was covered. Weighted at 6 it outranks every shape term put
       * together, which is right, because a hand you cannot see has no shape.
       */
      const final = cand.score + cand.clear * 6;
      if (final > bestFinal) {
        bestFinal = final;
        best = cand;
      }
    }
    if (!best) best = cands[0];
    this._handPosL.copy(best.pos);
    this._handQuatL.copy(best.quat);
    w[ads ? 'gripLFitAds' : 'gripLFit'] = { pos: best.pos.toArray(), quat: best.quat.toArray() };
    return true;
  }

  /**
   * STRAIGHTEN THE TRIGGER WRIST BY PITCHING THE HAND, not by moving the elbow.
   *
   * The two things round three asks for are, as authored, opposites. A small
   * wrist break needs the forearm to run along the hand's metacarpal axis; the
   * authored `gripR.finger` points 33 degrees below the horizontal, so that
   * means an elbow ABOVE the wrist — which is the chicken wing over the
   * shoulder the renders caught. An elbow below and right of the wrist, which
   * is where the reference photo has it, puts the forearm at 60-90 degrees to
   * that axis. No choice of elbow satisfies both, and the measurements say so:
   * constraining the elbow drove the break to 60 degrees and the hard clamp
   * then rotated the glove off the grip into mid-frame.
   *
   * The authored vector is what is wrong. On a pistol grip the metacarpals do
   * NOT run down the grip — they run from the wrist forward to the knuckles at
   * the front-top of it, and the FINGERS curl down and around. So the hand is
   * pitched up about its own knuckle axis until the wrist is inside budget,
   * and the palm arch is rotated back by the same angle, which returns every
   * finger and the thumb to exactly where the pose had them. The grip does not
   * move; only the 31 mm of palm behind the arch hinge lifts, which is what
   * lifts off the back strap when a real wrist straightens.
   *
   * Iterated, because the forearm direction depends on the hand orientation
   * that the solve is choosing: four passes is plenty, it converges fast.
   */
  /**
   * Size both gloves for this weapon. Has to run BEFORE the build-time fits,
   * because the support hand's contact solve measures the glove it is handed,
   * and again on every weapon change, because the two Arms are shared by all
   * fifteen weapons.
   */
  _applyHandScale(w) {
    const over = HAND_SCALE[w.id];
    for (let i = 0; i < 2; i++) {
      const arm = i ? this.armL : this.armR;
      const hs = over ? over[i] : arm.handScale;
      // `handInner` also carries the chirality mirror, so the sign survives.
      arm.handInner.scale.set(Math.sign(arm.handInner.scale.x) * hs, hs, hs);
    }
  }

  _alignTriggerWrist(w, def) {
    const gR = w.gripR;
    if (!gR) return;
    /**
     * TWICE: once at the hipfire pose and once at the ADS pose.
     *
     * The alignment is a function of where the forearm comes from, and ADS
     * moves the weapon to put its sight on the camera axis while the shoulder
     * sockets stay where the body is. That is a different arm, so a hand frame
     * solved at the hip is the wrong one down the sights: MEASURED, with a
     * single hipfire alignment the ADS trigger wrist ran 22-100 degrees across
     * the catalog against 22.8-24.5 at the hip. `_solveHands` blends the two
     * by the same `adsT` that blends the weapon pose, so the wrist is right at
     * both ends and travels smoothly between them.
     */
    // ADS first, hip second, so the arm is LEFT in the hipfire pose: whatever
    // runs next (the support-hand placement) inherits these sockets, and a
    // placement solved against the ADS arm is not the one the player sees most.
    this._alignBoth(w, def, true);
    this._alignBoth(w, def, false);
  }

  /**
   * @param {boolean} ads  pose the rig down the sights instead of at the hip
   */
  _alignBoth(w, def, ads) {
    const gR = w.gripR;
    /**
     * POSE THE RIG FIRST. This runs inside `addWeapon`, where the arm still
     * carries whatever shoulder and elbow bounds the last frame left on it —
     * at build time, the Arm constructor's defaults and no bounds at all. The
     * solve it was doing was therefore against an arm in a position no frame
     * ever shows, which is why its answer did not survive contact with one.
     *
     * The weapon's resting transform is hipPos + the global offset and hipRot,
     * which is exactly what `_poseWeapon` lands on at idle, so reconstruct it
     * here and put the sockets and the bounds where the first rendered frame
     * will have them.
     */
    const rigPos = new THREE.Vector3();
    const rigQuat = new THREE.Quaternion();
    if (ads) {
      // The same solve `_poseWeapon` does: cant the weapon, then translate so
      // its sight lands on the camera axis at the weapon's own eye relief.
      const cant = def?.adsCant ?? [0, 0, 0];
      _e.set(cant[0], cant[1], cant[2], 'XYZ');
      rigQuat.setFromEuler(_e);
      const sightLocal = w.sight.clone().applyQuaternion(rigQuat);
      rigPos.set(0, 0, -(def?.eyeRelief ?? 0.08)).sub(sightLocal);
    } else {
      rigPos.fromArray(def?.hipPos ?? [0.115, -0.18, -0.3]);
      rigPos.x += HIP_OFFSET.x;
      rigPos.y += HIP_OFFSET.y;
      rigPos.z += HIP_OFFSET.z;
      const hipR = def?.hipRot ?? [0, 0, 0];
      _e.set(hipR[0], hipR[1], hipR[2], 'XYZ');
      rigQuat.setFromEuler(_e);
    }
    const inv = rigQuat.clone().invert();
    // The eye, in WEAPON space, kept for `_placeSupportHand`'s visibility
    // probe. It is not simply -hipPos: the weapon is rotated by hipRot, so the
    // eye has to come back through that rotation too. Skipping it moved the
    // probe 45 mm, which on a pump shotgun is the difference between seeing
    // past the barrel and not — the probe reported 100% clear on a hand the
    // raster showed 97% covered.
    if (!ads) this._buildEye = new THREE.Vector3().sub(rigPos).applyQuaternion(inv);
    this.armR.shoulder.copy(this.shoulderR).sub(rigPos).applyQuaternion(inv);
    this.armL.shoulder.copy(this.shoulderL).sub(rigPos).applyQuaternion(inv);
    const pos = new THREE.Vector3().fromArray(gR.pos);
    const quat = new THREE.Quaternion();
    handBasis(quat, gR.finger ?? [0, -0.35, -0.94], gR.back ?? [0.95, 0.25, 0.18]);
    /**
     * DO NOT ALIGN A HAND THAT IS AT THE LENS. Rotating it cannot help and
     * will fill the frame with glove; see the note on ALIGN_MAX_ADS. 0.16 m is
     * a hand's own length, which is the distance at which turning one toward
     * the camera stops being a wrist correction and starts being a wall.
     */
    const atLens = (p) => p.clone().applyQuaternion(rigQuat).add(rigPos).z > -0.16;
    const budget = atLens(pos) ? 0 : ads ? ALIGN_MAX_ADS : ALIGN_MAX;
    const fit = this._alignWrist(this.armR, pos, quat, budget);
    const slot = ads ? 'gripRFitAds' : 'gripRFit';
    w[slot] = { quat: quat.toArray(), arch: fit.arch };

    /**
     * The SUPPORT hand gets the same treatment when it has no handguard to be
     * placed against — the pistols, the uzi, the knife, the fists. Those were
     * the weapons left at 90-100 degrees of support wrist after the trigger
     * hand was fixed, for exactly the same reason: an authored grip frame that
     * no elbow below the shoulder can line up with.
     */
    if (!w.model?.nodes?.handguard && w.gripL) {
      const lpos = new THREE.Vector3().fromArray(w.gripL.pos);
      const lquat = new THREE.Quaternion();
      handBasis(lquat, w.gripL.finger ?? [0.82, 0.5, -0.28], w.gripL.back ?? [-0.5, 0.32, -0.8]);
      /**
       * NO SUPPORT-WRIST ALIGNMENT DOWN THE SIGHTS when the support hand is
       * BEHIND the weapon's origin. The pistols hold the gun in both hands in
       * front of the face, and their support grip sits back on the frame at
       * z = +0.05 to +0.08 — right under the rear notch. At ADS every degree
       * of rotation on that hand walks its fingers across the slide and past
       * the front sight: MEASURED on the pistol, main shows 5 306 hand pixels
       * in its ADS frame and the aligned version showed 17 825, with fingers
       * over the slide. The wrist is worth less than the sight picture.
       *
       * The sign of z is what separates the two cases, and it is not a proxy:
       * a support grip forward of the origin (the rpg at -0.147, the smg at
       * -0.153) is a FOREND grip, a hand's length down the barrel and nowhere
       * near the sight line, and taking its alignment away only breaks the
       * wrist — it put the rpg's ADS support wrist at 87.8 degrees, over the
       * asserted budget.
       */
      const behind = (w.gripL.pos?.[2] ?? 0) > 0;
      const lbudget = (ads && behind) || atLens(lpos) ? 0 : ALIGN_MAX;
      const lfit = this._alignWrist(this.armL, lpos, lquat, lbudget);
      w[ads ? 'gripLFitAds' : 'gripLFit'] = { pos: lpos.toArray(), quat: lquat.toArray(), arch: lfit.arch };
    }
  }

  /**
   * Rotate `quat` until the hand it describes lines up with the forearm the
   * two-bone solve gives it, and report how much of that the palm arch can
   * take back. See `_alignTriggerWrist` for why this exists at all.
   *
   * @returns {{applied: number, arch: number}} radians
   */
  _alignWrist(arm, pos, quat, budget = ALIGN_MAX) {
    const spin = new THREE.Quaternion();
    const xAxis = new THREE.Vector3();
    const axis = new THREE.Vector3();
    const foreDir = new THREE.Vector3();
    const handZ = new THREE.Vector3();
    const scratch = new THREE.Quaternion();
    const limit = arm.wristLimit;
    let applied = 0;
    let archTake = 0;
    for (let pass = 0; pass < 8; pass++) {
      arm.solve(pos, quat);
      arm.root.updateMatrixWorld(true);
      foreDir.set(0, 0, -1).applyQuaternion(arm.forePivot.getWorldQuaternion(scratch)).normalize();
      handZ.set(0, 0, -1).applyQuaternion(quat).normalize();
      const brk = Math.acos(THREE.MathUtils.clamp(foreDir.dot(handZ), -1, 1));
      if (brk <= limit) break;
      /**
       * THE MINIMAL ROTATION, not a flexion.
       *
       * The first version only rotated about the hand's own knuckle axis, on
       * the reasoning that a wrist flexes. It does, but it also deviates, and
       * the forearm on this rig arrives from the lower RIGHT — mostly along
       * the knuckle axis, the one direction a pure flexion cannot correct.
       * MEASURED: it asked for 4.0 rad of flexion, saturated its cap at 2.0,
       * and still left the break at 63 degrees, because every pass was
       * rotating in a plane the error was not in. Spending the same budget on
       * cross(handZ, foreDir) takes the same weapon to 24.5 degrees.
       *
       * The roll about the hand's own -Z is untouched by that rotation, so the
       * palm still faces the grip.
       */
      axis.crossVectors(handZ, foreDir);
      if (axis.lengthSq() < 1e-12) break;
      axis.normalize();
      const step = Math.min(brk - limit, budget - applied);
      if (step <= 1e-4) break;
      spin.setFromAxisAngle(axis, step);
      quat.premultiply(spin);
      applied += step;
      // How much of that was flexion about the knuckle row — the only part the
      // palm arch can take back without moving the fingers off the grip.
      xAxis.set(1, 0, 0).applyQuaternion(quat).normalize();
      archTake += step * axis.dot(xAxis);
    }
    return { applied, arch: archTake * ALIGN_COMP };
  }

  _fitSupportHand(w, def, ads = false) {
    const hg = w.model.nodes.handguard;
    const gL = w.gripL;
    if (!hg || !gL || w.id === 'pistol') return;
    if (!this._placeSupportHand(w, def, ads)) {
      this._handPosL.fromArray(gL.pos);
      handBasis(this._handQuatL, gL.finger ?? [0.82, 0.5, -0.28], gL.back ?? [-0.5, 0.32, -0.8]);
    }
    const poseName = ads ? `clamp:${w.id}:ads` : `clamp:${w.id}`;
    this.armL.setPose('clamp');
    const contacts = this.armL.fitToCylinder(
      this._handPosL,
      this._handQuatL,
      hg.axis,
      hg.dir,
      hg.r,
      { clearance: 0.001, poseName }
    );
    if (ads) w.lhandPoseAds = poseName;
    else w.lhandPose = poseName;
    // Only keep contacts that actually landed on the handguard's own extent —
    // a fingertip that overshot past the end cap must not paint AO on the barrel.
    const z0 = Math.max(hg.z0, hg.z1);
    const z1 = Math.min(hg.z0, hg.z1);
    const kept = contacts.filter((p) => p.z <= z0 + 0.012 && p.z >= z1 - 0.012);
    if (!ads) {
      this.armL.bakeContactAO(kept, 0.012, 0.7);
      this._bakeContactAOOnWeapon(w, kept, 0.012, 0.9);
    }
    this.armL.setPose(poseName);
  }

  /**
   * The weapon side of the same contact gradient. The handguard geometry already
   * carries wear/grime/AO masks from `bakeMasks`, so this only ever RAISES the
   * AO channel — the edge-wear and grime layers are untouched.
   */
  _bakeContactAOOnWeapon(w, contacts, radius, peak) {
    if (!contacts.length) return;
    const r2 = radius * radius;
    for (const mesh of w.meshes) {
      const geo = mesh.geometry;
      const pos = geo.getAttribute('position');
      const col = geo.getAttribute('color');
      if (!pos || !col) continue;
      for (let i = 0; i < pos.count; i++) {
        _v.fromBufferAttribute(pos, i);
        let closest = Infinity;
        for (const c of contacts) {
          const d2 = _v.distanceToSquared(c);
          if (d2 < closest) closest = d2;
        }
        if (closest > r2) continue;
        const t = 1 - Math.sqrt(closest) / radius;
        const s = t * t * t * (t * (t * 6 - 15) + 10);
        const k = i * 3 + 2;
        col.array[k] = Math.max(col.array[k], peak * s);
      }
      col.needsUpdate = true;
    }
  }

  setActive(id) {
    const w = this.weapons.get(id);
    if (!w || w === this.active) return this.active;
    this._applyHandScale(w);
    if (this.active) this.active.group.visible = false;
    this.active = w;
    w.group.visible = true;
    this.recPos.reset();
    this.recRot.reset();
    this.settle.reset();
    this.boltCycle = 0;
    this.boltHold = 0;
    this.magInHand = 0;
    this.magVisible = true;
    this.armR.setPose('grip');
    // The FITTED clamp for this weapon, not the authored one — see _fitSupportHand.
    this.armL.setPose(w.lhandPose ?? (id === 'pistol' ? 'cup' : 'clamp'));
    return w;
  }

  /* ====================================================================== */
  /*  clip playback                                                         */
  /* ====================================================================== */

  play(name) {
    const w = this.active;
    if (!w) return 0;
    const clip = w.clips[name];
    if (!clip) return 0;
    this.clip = clip;
    this.clipT = 0;
    this.clipPrevT = -1;
    return clip.duration;
  }

  stopClip() {
    this.clip = null;
    this.clipResult.active = false;
    this.clipResult.lhand.weight = 0;
  }

  get clipPlaying() {
    return this.clip !== null;
  }

  get clipName() {
    return this.clip?.name ?? null;
  }

  /* ====================================================================== */
  /*  impulses                                                              */
  /* ====================================================================== */

  /**
   * Per-shot viewmodel kick. `pitch`/`yaw` are the aim-space recoil for this
   * shot (from the deterministic pattern) so the visual climb matches where the
   * bullets are actually going.
   */
  addRecoil(pitch, yaw, first = false) {
    const w = this.active;
    if (!w) return;
    const r = w.def.recoil;
    const ads = this.adsT;
    // Aiming braces the weapon: less travel, faster return.
    const scale = lerp(1, 0.54, ads) * (first ? 1.18 : 1);
    const jitter = 0.86 + this.rng.float() * 0.3;
    this.recPos.f = r.freq;
    this.recPos.z = r.damping;
    this.recRot.f = r.freq * 0.92;
    this.recRot.z = r.damping;
    // A velocity impulse of v0 on a spring of angular frequency w peaks at
    // roughly v0/w, so the kick amplitudes below are in real metres/radians.
    const wp = TAU * this.recPos.f;
    const wr = TAU * this.recRot.f;
    this.recPos.kick(
      this.rng.signed() * r.kickBack * 0.2 * scale * wp,
      r.kickUp * scale * jitter * wp,
      r.kickBack * scale * jitter * wp
    );
    this.recRot.kick(
      (pitch * 5.5 + r.pitch * 1.4) * scale * jitter * wr,
      (-yaw * 4.5 - this.rng.signed() * r.yaw * 0.8) * scale * wr,
      (this.rng.signed() * 0.4 + 0.6) * r.roll * scale * wr
    );
    // Slow settling drift after a burst — the muzzle keeps wandering a little.
    const ws = TAU * this.settle.f;
    this.settle.kick(
      this.rng.signed() * 0.0012 * scale * ws,
      0.0018 * scale * ws,
      this.rng.signed() * 0.003 * scale * ws
    );
    this.boltCycle = 1;
  }

  jump() {
    this.jumpSpring.kick(-1.2);
  }

  land(speed = 3) {
    this.landSpring.kick(clamp(speed * 0.45, 0.4, 3.4));
  }

  /* ====================================================================== */
  /*  frame update                                                          */
  /* ====================================================================== */

  /**
   * @param {number} dt
   * @param {object} s  { ads, sprint, lowReady, speed, crouch, airborne,
   *                      trigger, empty, cycleTime }
   */
  update(dt, s) {
    const w = this.active;
    if (!w) return;
    const def = w.def;
    // Defensive: a non-positive or absurd dt would integrate the whole
    // animation stack backwards (a negative step snaps ADS straight to 1).
    dt = dt > 0 ? (dt < 0.1 ? dt : 0.1) : 0;

    /* -------- camera-relative anchor ---------------------------------- */
    const cam = this.ctx.camera;
    const vcam = this.ctx.viewCamera;
    if (this.trackCamera) {
      cam.updateMatrixWorld();
      this.anchor.position.setFromMatrixPosition(cam.matrixWorld);
      this.anchor.quaternion.setFromRotationMatrix(cam.matrixWorld);
      // Keep the viewmodel camera coincident with the world camera: the renderer
      // uses that to decide the gun can share the world's shadow cascades.
      vcam.position.copy(this.anchor.position);
      vcam.quaternion.copy(this.anchor.quaternion);
    }

    /* -------- angular velocity for the lag layer ---------------------- */
    _e.setFromQuaternion(this.anchor.quaternion, 'YXZ');
    const yaw = _e.y;
    const pitch = _e.x;
    if (this._hasPrev && dt > 1e-5) {
      const dy = wrapPi(yaw - this._prevYaw) / dt;
      const dp = wrapPi(pitch - this._prevPitch) / dt;
      // Low-pass, then clamp: a teleport must not throw the gun off screen.
      this._angVel.yaw = damp(this._angVel.yaw, clamp(dy, -9, 9), 18, dt);
      this._angVel.pitch = damp(this._angVel.pitch, clamp(dp, -9, 9), 18, dt);
    } else {
      this._angVel.yaw = 0;
      this._angVel.pitch = 0;
    }
    this._prevYaw = yaw;
    this._prevPitch = pitch;
    this._hasPrev = true;

    /* -------- blends --------------------------------------------------- */
    const adsRate = 1 / Math.max(0.05, def.adsTime);
    const wantAds = this.clip && this.clip.name !== 'draw' ? 0 : s.ads ? 1 : 0;
    this.adsTarget = wantAds;
    // Linear rate with a smootherstep shaping: a spring here reads as mushy.
    this.adsT = clamp01(this.adsT + (wantAds ? adsRate : -adsRate * 1.25) * dt);
    const ads = smootherstep(0, 1, this.adsT);

    const sprintTarget = s.sprint && !this.clip ? 1 : 0;
    this.sprintT = damp(this.sprintT, sprintTarget, 9, dt);
    this.lowReadyT = damp(this.lowReadyT, s.lowReady ? 1 : 0, 8, dt);

    this.triggerTarget = s.trigger ? 1 : 0;
    this.triggerT = damp(this.triggerT, this.triggerTarget, 26, dt);

    /* -------- base pose ------------------------------------------------ */
    const hipP = def.hipPos;
    const hipR = def.hipRot;
    this._basePos.set(hipP[0] + HIP_OFFSET.x, hipP[1] + HIP_OFFSET.y, hipP[2] + HIP_OFFSET.z);
    _e.set(hipR[0], hipR[1], hipR[2], 'XYZ');
    this._baseQuat.setFromEuler(_e);

    // Sprint / low-ready poses replace the hip pose.
    if (this.sprintT > 1e-3) {
      const p = def.sprintPos;
      const r = def.sprintRot;
      this._tmpPos.set(p[0], p[1], p[2]);
      _e.set(r[0], r[1], r[2], 'XYZ');
      this._tmpQuat.setFromEuler(_e);
      this._basePos.lerp(this._tmpPos, this.sprintT);
      this._baseQuat.slerp(this._tmpQuat, this.sprintT);
    }
    if (this.lowReadyT > 1e-3) {
      const p = def.lowReadyPos;
      const r = def.lowReadyRot;
      this._tmpPos.set(p[0], p[1], p[2]);
      _e.set(r[0], r[1], r[2], 'XYZ');
      this._tmpQuat.setFromEuler(_e);
      this._basePos.lerp(this._tmpPos, this.lowReadyT);
      this._baseQuat.slerp(this._tmpQuat, this.lowReadyT);
    }

    /* -------- ADS pose: solved, not authored --------------------------- */
    if (ads > 1e-4) {
      const cant = def.adsCant;
      _e.set(cant[0], cant[1], cant[2], 'XYZ');
      this._adsQuat.setFromEuler(_e);
      // sight point in rig space, then the translation that lands it on axis
      this._sightLocal.copy(w.sight).applyQuaternion(this._adsQuat);
      this._adsPos.set(0, 0, -def.eyeRelief).sub(this._sightLocal);
      this._basePos.lerp(this._adsPos, ads);
      this._baseQuat.slerp(this._adsQuat, ads);
    }

    /* -------- additive layers ------------------------------------------ */
    const swayScale = def.swayScale * lerp(1, 0.22, ads) * lerp(1, 1.5, this.sprintT);
    this.noiseT += dt;
    const n = this.noise;
    const nr = this.noiseRates;
    const t = this.noiseT;
    // Layered, incommensurate rates: the pattern does not repeat in a session.
    const swayX = n[0].fbm(t * nr[0], 3) * 0.55 + n[3].fbm(t * nr[3] * 2.3, 2) * 0.45;
    const swayY = n[1].fbm(t * nr[1], 3) * 0.55 + n[4].fbm(t * nr[4] * 2.1, 2) * 0.45;
    const swayZ = n[2].fbm(t * nr[2], 2) * 0.6 + n[5].fbm(t * nr[5] * 1.7, 2) * 0.4;
    // Breathing: a slow 0.22 Hz cycle under the noise.
    const breath = Math.sin(t * 1.38) * 0.5 + Math.sin(t * 0.61 + 1.1) * 0.25;

    let px = swayX * 0.0075 * swayScale;
    let py = (swayY * 0.006 + breath * 0.0022) * swayScale;
    let pz = swayZ * 0.004 * swayScale;
    let rx = (swayY * 0.021 + breath * 0.006) * swayScale;
    let ry = swayX * 0.028 * swayScale;
    let rz = swayZ * 0.017 * swayScale;

    /* -------- movement bob --------------------------------------------- */
    const speed = s.speed ?? 0;
    this._moveSpeed = speed;
    const bobAmt =
      def.bobScale * clamp01(speed / 4.2) * lerp(1, 0.28, ads) * (s.airborne ? 0.25 : 1);
    if (speed > 0.05) {
      // Stride frequency scales with speed; sprint takes longer strides.
      this.bobPhase += dt * (3.1 + speed * 0.72) * (s.sprint ? 1.05 : 1);
      if (this.bobPhase > TAU * 64) this.bobPhase -= TAU * 64;
    }
    const bp = this.bobPhase;
    px += Math.sin(bp) * 0.0165 * bobAmt;
    py += (Math.abs(Math.cos(bp)) - 0.6) * 0.0125 * bobAmt;
    pz += Math.sin(bp * 2) * 0.0055 * bobAmt;
    rz += Math.sin(bp) * 0.031 * bobAmt;
    rx += Math.cos(bp * 2) * 0.014 * bobAmt;
    ry += Math.sin(bp + 0.6) * 0.019 * bobAmt;

    /* -------- weapon lag ---------------------------------------------- */
    const lagScale = lerp(1, 0.42, ads);
    const av = this._angVel;
    this.lag.step(
      dt,
      clamp(-av.yaw * 0.019, -0.05, 0.05) * lagScale,
      clamp(av.pitch * 0.014, -0.04, 0.04) * lagScale,
      clamp(-Math.abs(av.yaw) * 0.006, -0.03, 0.03) * lagScale
    );
    this.lagRot.step(
      dt,
      clamp(-av.pitch * 0.075, -0.24, 0.24) * lagScale,
      clamp(av.yaw * 0.085, -0.3, 0.3) * lagScale,
      clamp(-av.yaw * 0.055, -0.2, 0.2) * lagScale
    );
    px += this.lag.x;
    py += this.lag.y;
    pz += this.lag.z;
    rx += this.lagRot.x;
    ry += this.lagRot.y;
    rz += this.lagRot.z;

    /* -------- recoil + settle ----------------------------------------- */
    this.recPos.step(dt, 0, 0, 0);
    this.recRot.step(dt, 0, 0, 0);
    this.settle.step(dt, 0, 0, 0);
    px += this.recPos.x;
    py += this.recPos.y;
    pz += this.recPos.z;
    rx += this.recRot.x + this.settle.y;
    ry += this.recRot.y + this.settle.x;
    rz += this.recRot.z + this.settle.z;

    /* -------- jump / land --------------------------------------------- */
    this.jumpSpring.step(dt, 0);
    this.landSpring.step(dt, 0);
    py -= this.landSpring.x * 0.014 + this.jumpSpring.x * 0.006;
    rx -= this.landSpring.x * 0.05;

    /* -------- clip (reload / inspect / draw) -------------------------- */
    const res = this.clipResult;
    if (this.clip) {
      this.clipT += dt;
      const c = this.clip;
      const tt = clamp(this.clipT, 0, c.duration);
      c.sample(tt, res);
      for (const ev of c.events) {
        if (ev.t > this.clipPrevT && ev.t <= tt) this.onClipEvent?.(ev.name, c.name);
      }
      this.clipPrevT = tt;
      px += res.pos[0];
      py += res.pos[1];
      pz += res.pos[2];
      rx += res.rot[0];
      ry += res.rot[1];
      rz += res.rot[2];
      if (this.clipT >= c.duration) {
        this.stopClip();
      }
    }

    /* -------- compose -------------------------------------------------- */
    this.rig.position.set(
      this._basePos.x + px,
      this._basePos.y + py,
      this._basePos.z + pz
    );
    _e.set(rx, ry, rz, 'XYZ');
    _q.setFromEuler(_e);
    this.rig.quaternion.copy(this._baseQuat).multiply(_q);
    // The standalone preview harness pins the rig so the weapon can be framed
    // in its own space; everything downstream reads the composed transform.
    if (this.rigOverride) {
      this.rig.position.copy(this.rigOverride.position);
      this.rig.quaternion.copy(this.rigOverride.quaternion);
    }
    this.rig.updateMatrix();
    this.rig.updateMatrixWorld(true);

    /* -------- hands (first: the magazine can be held by one) ---------- */
    this._solveHands(w, res);

    /* -------- moving parts -------------------------------------------- */
    this._updateParts(w, dt, s, res);

    /* -------- reticle -------------------------------------------------- */
    this._updateReticle(w, ads);

    /* -------- viewmodel FOV ------------------------------------------- */
    const fovBase = 60;
    const targetFov = fovBase * lerp(1, def.viewFov, ads);
    if (Math.abs(vcam.fov - targetFov) > 1e-3) {
      vcam.fov = targetFov;
      vcam.updateProjectionMatrix();
    }
  }

  /* ---------------------------------------------------------------------- */

  _updateParts(w, dt, s, res) {
    const p = w.parts;

    // Bolt / slide cycle: a fast rearward stroke and a slightly slower return.
    if (this.boltCycle > 0) {
      const cycle = Math.max(0.045, (w.def.cycleTime ?? 60 / w.def.rpm) * 0.62);
      this.boltCycle = Math.max(0, this.boltCycle - dt / cycle);
    }
    const cyc = this.boltCycle;
    // 1 -> 0 over the cycle: out fast, back with a small bounce.
    const stroke = cyc > 0.55 ? (1 - cyc) / 0.45 : cyc / 0.55;
    const clipBolt = res.active ? res.parts.bolt : 0;
    const boltOff = Math.max(stroke, this.boltHold, clipBolt * this.boltHold);

    if (p.bolt) {
      p.bolt.position.set(
        w.model.nodes.boltRest.pos[0] + w.boltTravel.x * boltOff,
        w.model.nodes.boltRest.pos[1] + w.boltTravel.y * boltOff,
        w.model.nodes.boltRest.pos[2] + w.boltTravel.z * boltOff
      );
    }
    if (p.slide) {
      p.slide.position.set(
        w.model.nodes.slideRest.pos[0] + w.slideTravel.x * boltOff,
        w.model.nodes.slideRest.pos[1] + w.slideTravel.y * boltOff,
        w.model.nodes.slideRest.pos[2] + w.slideTravel.z * boltOff
      );
    }
    if (p.charging) {
      const pull = res.active ? res.parts.charge : 0;
      const rest = w.model.nodes.chargeRest.pos;
      p.charging.position.set(
        rest[0] + w.chargePull.x * pull,
        rest[1] + w.chargePull.y * pull,
        rest[2] + w.chargePull.z * pull
      );
    }
    if (p.trigger) {
      p.trigger.rotation.x = w.triggerPull * this.triggerT;
    }
    if (p.selector) {
      p.selector.rotation.x = lerp(-0.95, 0, clamp01(this.selectorLive ?? 1));
    }

    // Magazine: seated, in the support hand, or hidden.
    if (p.magazine) {
      const inHand = res.active ? res.parts.mag : 0;
      this.magVisible = res.active ? res.parts.magVisible : true;
      p.magazine.visible = this.magVisible;
      if (inHand > 1e-4) {
        // Follow the support hand: the magazine is gripped by its spine.
        this._magFromHand(w, p.magazine, inHand);
      } else {
        p.magazine.position.copy(w.magSeatPos);
        p.magazine.quaternion.copy(w.magSeatQuat);
      }
    }
  }

  _magFromHand(w, magGroup, weight) {
    // The hand target is a WRIST in weapon space, so the magazine has to be
    // offset into the palm (about 62 mm along the hand's -Z, the metacarpal
    // axis) before the along-the-magazine offset — otherwise the mag is gripped
    // by thin air behind the hand.
    _q.copy(this._handQuatL);
    _v.copy(this._handPosL);
    _v2.set(0, w.magLen * 0.62, -0.062).applyQuaternion(_q);
    _v.add(_v2);
    magGroup.position.lerpVectors(w.magSeatPos, _v, weight);
    _q2.copy(w.magSeatQuat).slerp(_q, weight);
    magGroup.quaternion.copy(_q2);
  }

  /**
   * One pass of the build-time wrist alignment, for a hand an animation clip
   * is driving. See `_alignWrist`, which this is the runtime twin of: the same
   * minimal rotation about cross(handZ, foreDir), the same budget, the same
   * refusal to touch a hand that is already at the lens.
   *
   * @param {THREE.Vector3} pos    hand target, rig space (not modified)
   * @param {THREE.Quaternion} quat  hand orientation, rotated in place
   */
  _relaxWrist(arm, pos, quat) {
    arm.root.updateMatrixWorld(true);
    _relaxA.set(0, 0, -1).applyQuaternion(arm.forePivot.getWorldQuaternion(_q2)).normalize();
    _relaxB.set(0, 0, -1).applyQuaternion(quat).normalize();
    const brk = Math.acos(THREE.MathUtils.clamp(_relaxA.dot(_relaxB), -1, 1));
    if (brk <= arm.wristLimit) return;
    _relaxAxis.crossVectors(_relaxB, _relaxA);
    if (_relaxAxis.lengthSq() < 1e-12) return;
    _relaxAxis.normalize();
    _q2.setFromAxisAngle(_relaxAxis, Math.min(brk - arm.wristLimit, ALIGN_MAX));
    quat.premultiply(_q2);
    arm.solve(pos, quat);
  }

  _solveHands(w, res) {
    // Shoulders are body-fixed: express the camera-space anchor in rig space.
    _q.copy(this.rig.quaternion).invert();
    // The hands stay planted on the weapon, but the torso and shoulders carry a
    // small counter-swing during locomotion. Solving the elbows from these
    // moving sockets flexes the arm instead of shaking the whole viewmodel as
    // one rigid prop — this is the "arms shake naturally as the player walks"
    // half of card 42f58873. Left/right are deliberately out of phase.
    /**
     * THE TORSO WALKS; THE ARMS ARE WHAT SHOWS IT.
     *
     * "Make the arms shake naturally as the player walks" cannot be done by
     * moving the viewmodel, because the viewmodel is the thing the hands are
     * welded to: translate the rig and the hands, the forearms and the weapon
     * all move together, which is a camera shake, not an arm. MEASURED on the
     * previous version, four frames across a stride: a single rigid 2-D
     * translation explained 95-98% of everything that changed on screen, and
     * the shoulder sockets were being offset by 8.0 / 4.5 / 3.5 mm against a
     * rig bob of 16.5 mm — so the sockets were not even keeping up with the
     * translation they were supposed to be counteracting.
     *
     * What a walking shooter actually does is rotate his SHOULDER GIRDLE: the
     * chest counter-rotates against the hips once per stride, rolls onto each
     * foot at twice that rate, and drops on every heel strike. The hands are
     * on the gun, so every bit of that has to be absorbed by the elbows, which
     * is exactly the articulation the card is asking to see.
     *
     * So the two sockets are carried by one rigid torso transform about a chest
     * pivot, in camera space, before being re-based into the rig's. The numbers
     * are small in isolation — 0.13 rad of yaw on a 0.21 m lever is 27 mm of
     * shoulder travel — but they are differential, which the rig bob is not:
     * the elbow swings several times that, and the forearm's on-screen angle
     * changes with it.
     */
    const gait = clamp01((this._moveSpeed ?? 0) / 4.2);
    /**
     * AMPLITUDES, measured rather than chosen, and then measured again against
     * the wrist. A walking shooter counter-rotates his shoulder girdle 15-20
     * degrees, which is 0.30 rad, and at that the support forearm's on-screen
     * angle swings 16 degrees across a stride — plenty. But the sockets move
     * while the hands stay welded to the weapon, so every degree of torso goes
     * into the wrists too, and at 0.30 rad the trigger wrist ran 40 degrees
     * mid-stride against a 35 budget on nine weapons. SWEPT:
     *
     *   yaw 0.30   forearm swing 16.7 deg, worst walk wrist 41.6
     *   yaw 0.17   12.5, 37.2
     *   yaw 0.15   ~11,  ~36      <- chosen
     *   yaw 0.13    9.7, 36.1
     *
     * The gait is still doing its job — a rigid translation explains under a
     * tenth of the elbow's travel — and the wrist is nearly inside budget.
     * Most of what is left over is two weapons whose anchors are wrong
     * anyway (the sawnoff and the knife), not the gait.
     */
    const yaw = Math.sin(this.bobPhase) * 0.15 * gait;
    const roll = Math.sin(this.bobPhase * 2 + 0.9) * 0.09 * gait;
    const lift = Math.cos(this.bobPhase * 2) * 0.032 * gait;
    const sway = Math.sin(this.bobPhase + 0.6) * 0.026 * gait;
    /**
     * The recoil ROTATION has to be followed as well as the translation, and
     * on the short shotguns it is the bigger half: the sawnoff's kick pitches
     * the whole rig, which swings the hand target through an arc the fixed
     * sockets cannot follow, and the elbow goes over the shoulder to keep up.
     */
    _torsoE.set(
      yaw * 0 + this.recRot.x * 0.62,
      yaw + this.recRot.y * 0.62,
      roll + this.recRot.z * 0.62,
      'YZX'
    );
    _torsoQ.setFromEuler(_torsoE);
    /**
     * THE BODY ABSORBS RECOIL; the sockets must too.
     *
     * Recoil translates the rig, and the sockets are body-fixed, so without
     * this the weapon is driven back into an arm that does not move with it
     * and the chain folds. MEASURED on the sawnoff mid-recoil: the trigger
     * elbow ended 163 mm ABOVE its own shoulder — the chicken wing again, for
     * one fiftieth of a second per shot, on the weapon with the hardest kick.
     *
     * 0.62 of the recoil offset, and only the RECOIL offset. The walk bob must
     * keep going into the elbows or the articulation this card is about
     * disappears (see the torso block above), so the two cannot share a term:
     * `recPos` is the recoil spring alone and is what gets followed.
     */
    _recoil.set(this.recPos.x, this.recPos.y, this.recPos.z).multiplyScalar(0.62);
    /** Shoulder socket -> torso-rotated -> rig space. */
    const socket = (src, out) => {
      out.copy(src).sub(_chest).applyQuaternion(_torsoQ).add(_chest);
      out.x += sway + _recoil.x;
      out.y += lift + _recoil.y;
      out.z += _recoil.z;
      out.sub(this.rig.position).applyQuaternion(_q);
      return out;
    };
    this.armR.shoulder.copy(socket(this.shoulderR, _v));
    this.armL.shoulder.copy(socket(this.shoulderL, _v));
    /**
     * Walk the elbows around their own circles, out of phase with each other
     * and with the torso. See `Arm.solve`: a socket that moves cannot bend an
     * arm that is already straight, but the elbow it chooses can swing the
     * forearm through tens of degrees with the hand welded where it is.
     */
    this.armR.elbowPhiBias = Math.sin(this.bobPhase) * 0.6;
    this.armL.elbowPhiBias = Math.sin(this.bobPhase + 2.1) * 1.15;
    this.armR.elbowGaitWeight = 0.5 * gait;
    this.armL.elbowGaitWeight = 1.35 * gait;

    // ---- shooting hand: welded to the grip ----
    const gR = w.gripR;
    this._handPos.fromArray(gR.pos);
    if (w.gripRFit) {
      // Build-time wrist alignment, hip and ADS, blended by the same `adsT`
      // the weapon pose uses; see `_alignTriggerWrist`.
      this._handQuat.fromArray(w.gripRFit.quat);
      let arch = w.gripRFit.arch;
      if (w.gripRFitAds && this.adsT > 1e-4) {
        _q2.fromArray(w.gripRFitAds.quat);
        this._handQuat.slerp(_q2, this.adsT);
        arch += (w.gripRFitAds.arch - arch) * this.adsT;
      }
      this.armR.archOffset = arch;
    } else {
      handBasis(this._handQuat, gR.finger ?? [0, -0.35, -0.94], gR.back ?? [0.95, 0.25, 0.18]);
      this.armR.archOffset = 0;
    }
    // The arch offset is part of the pose, so re-apply it every frame.
    this.armR.setPose('grip');
    this.armR.solve(this._handPos, this._handQuat);
    this.armR.setTrigger(this.triggerT);

    // ---- support hand: grip, or wherever the clip puts it ----
    const gL = w.gripL;
    let pos = gL.pos;
    let finger = gL.finger ?? [0.82, 0.5, -0.28];
    let back = gL.back ?? [-0.5, 0.32, -0.8];
    let pose = (this.adsT > 0.5 ? w.lhandPoseAds : null) ?? w.lhandPose ?? (w.id === 'pistol' ? 'cup' : 'clamp');
    let fit = w.gripLFit ?? null;
    if (res.active && res.lhand.weight > 0.5) {
      pos = res.lhand.pos;
      finger = res.lhand.finger;
      back = res.lhand.back;
      pose = res.lhand.pose;
      fit = null;
    }
    if (fit) {
      // The build-time placement solve owns the handguard grip; see
      // `_placeSupportHand`. Reload clips drive the hand from their own keys.
      this._handPosL.fromArray(fit.pos);
      this._handQuatL.fromArray(fit.quat);
      if (w.gripLFitAds && this.adsT > 1e-4 && fit === w.gripLFit) {
        _v2.fromArray(w.gripLFitAds.pos);
        this._handPosL.lerp(_v2, this.adsT);
        _q2.fromArray(w.gripLFitAds.quat);
        this._handQuatL.slerp(_q2, this.adsT);
      }
      this.armL.archOffset = fit.arch ?? 0;
    } else {
      this.armL.archOffset = 0;
      this._handPosL.set(pos[0], pos[1], pos[2]);
      handBasis(this._handQuatL, finger, back);
    }
    if (pose !== this.armL.pose) this.armL.setPose(pose);
    this.armL.solve(this._handPosL, this._handQuatL);
    /**
     * RELAX THE WRIST ON ANIMATION KEYS TOO.
     *
     * The grips get their wrist straightened once, at build time, because
     * their frames never move. A reload clip's do: `buildMuzzleReloads` and
     * friends author a `finger`/`back` pair per station, against whatever
     * hands existed when they were written, and the arm has to follow them
     * wherever they lead. MEASURED on the rpg's muzzle load, which is the
     * worst of them: the support wrist sat at 62-78 degrees for the whole clip
     * and spiked to 93 on the return to the firing grip.
     *
     * So the same minimal rotation is applied here, with the same budget, when
     * a clip is driving the hand: solve, measure, rotate the frame toward the
     * forearm, solve again. Two solves for one arm on reload frames only. The
     * hand POSITION is untouched, so it stays on its station and the clip's
     * timing is unchanged — only the roll of the wrist gives.
     */
    if (!fit) this._relaxWrist(this.armL, this._handPosL, this._handQuatL);
  }

  /**
   * The collimated dot.
   *
   * A red dot sight is a collimator: the reticle sits at optical infinity along
   * the tube axis, so its apparent direction from the eye is the tube axis —
   * independent of where the eye is. Reproducing that exactly (rather than
   * gluing a sprite to the glass) is why the dot stays on target while the
   * weapon sways, and why it vignettes out when you look through the tube from
   * an angle.
   */
  _updateReticle(w, ads) {
    const optic = w.optic;
    if (!optic) {
      this.reticle.visible = false;
      return;
    }
    // Optic axis and lens centre, both in camera space. The weapon group is a
    // child of the rig which is a child of the anchor, so camera space is just
    // the rig transform applied to the weapon-local values — no inverses, no
    // allocation.
    _v.fromArray(optic.center).applyQuaternion(this.rig.quaternion).add(this.rig.position);
    _v3.set(0, 0, -1).applyQuaternion(this.rig.quaternion).normalize();

    // Where the axis ray from the eye crosses the lens plane.
    const s = _v.dot(_v3);
    if (s <= 0.02) {
      this.reticle.visible = false;
      return;
    }
    _v2.copy(_v3).multiplyScalar(s); // dot position in camera space
    // Vignette: how far off the lens centre the apparent dot lands.
    const offX = _v2.x - _v.x;
    const offY = _v2.y - _v.y;
    const off = Math.hypot(offX, offY);
    const apertureR = optic.apertureR ?? 0.01;
    let alpha = 1 - smootherstep(apertureR * 0.5, apertureR * 1.05, off);
    alpha *= lerp(0.55, 1, ads); // brighter once the eye is behind the glass

    if (alpha <= 0.01) {
      this.reticle.visible = false;
      return;
    }
    this.reticle.visible = true;
    this.reticle.position.copy(_v2);
    this.reticle.lookAt(this.anchor.getWorldPosition(_v));
    /**
     * SIZE. Angular, so it is FOV-independent within a stance — but not constant
     * across stances, because the requirement is a fixed number of PIXELS.
     *
     * A geometrically honest 2 MOA emitter subtends 0.58 mrad, which at the
     * viewmodel camera's 0.97 mrad/px (60 deg over 1080 px) is 0.6 px: a dead
     * subpixel, which is exactly what the old 0.0012 rad dot measured as. Every
     * shipped red dot cheats this, and cheats it in the same direction — the
     * reticle is drawn at a legible size and grows as you come into the glass,
     * because that is the perceptual experience of putting your eye behind a
     * collimator. So:
     *   hipfire  0.00385 rad -> 4.0 px radius,  7.9 px dot
     *   ADS      0.00655 rad -> 7.9 px radius, 15.7 px dot   (0.83 mrad/px)
     * with the halo at 1.6x and the segmented ring at 3.2x, both scaled off the
     * same number so the reticle never changes shape.
     */
    const coreR = s * lerp(0.00385, 0.00655, ads);
    this.dotCore.scale.setScalar(coreR);
    this.dotRim.scale.setScalar(coreR);
    this.dotHalo.scale.setScalar(coreR);
    this.dotRing.scale.setScalar(coreR);
    this.dotCore.material.opacity = alpha;
    this.dotRim.material.opacity = alpha * 0.8;
    this.dotRing.material.opacity = alpha;
    // The halo is a bloom seed, not a glow: 6% at 1.6x the core radius adds ~1 px
    // of soft falloff and nothing else.
    this.dotHalo.material.opacity = alpha * 0.06;
  }

  /* ====================================================================== */
  /*  world-space queries for firing                                        */
  /* ====================================================================== */

  /** Muzzle position in WORLD space (for the flash and the shell). */
  muzzleWorld(out) {
    const w = this.active;
    if (!w) return out.set(0, 0, 0);
    w.group.updateMatrixWorld();
    out.copy(w.muzzle).applyMatrix4(w.group.matrixWorld);
    // viewScene space == world space because the anchor tracks the camera.
    return out;
  }

  ejectWorld(out) {
    const w = this.active;
    if (!w) return out.set(0, 0, 0);
    w.group.updateMatrixWorld();
    out.copy(w.eject).applyMatrix4(w.group.matrixWorld);
    return out;
  }

  ejectVelocity(out, speed = 2.6) {
    const w = this.active;
    if (!w) return out.set(0, 0, 0);
    out.copy(w.ejectDir).transformDirection(w.group.matrixWorld).multiplyScalar(speed);
    return out;
  }

  /** Bore direction in world space. */
  boreDir(out) {
    const w = this.active;
    if (!w) return out.set(0, 0, -1);
    out.set(0, 0, -1).transformDirection(w.group.matrixWorld).normalize();
    return out;
  }

  dispose() {
    for (const w of this.weapons.values()) {
      for (const m of w.meshes) m.geometry.dispose();
    }
    this.weapons.clear();
    this.armL.dispose();
    this.armR.dispose();
    for (const g of this._reticleGeo) g.dispose();
    this.anchor.removeFromParent();
  }
}

function applyNode(obj, node) {
  obj.position.fromArray(node.pos);
  if (node.rot) obj.rotation.fromArray(node.rot);
}
